/**
 * System Settings Component (TASK-051)
 *
 * Form for managing system settings:
 * - Language selection
 * - Timezone
 * - Date format
 * - Currency
 * - Pagination size
 * - Notification toggles
 */
import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  SystemSettings as SystemSettingsType,
  Language,
  Currency,
  DateFormat,
} from '@/types/settings';
import { LANGUAGES, CURRENCIES, DATE_FORMATS } from '@/types/settings';

interface SystemSettingsProps {
  settings: SystemSettingsType;
  onChange: (updates: Partial<SystemSettingsType>) => void;
  onSave: () => void;
  disabled: boolean;
  paginationError: string | null;
}

/**
 * Common timezone options
 */
const TIMEZONES = [
  { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK)' },
  { value: 'Europe/London', label: 'Europe/London (GMT)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
  { value: 'America/New_York', label: 'America/New York (EST)' },
  { value: 'America/Los_Angeles', label: 'America/Los Angeles (PST)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST)' },
  { value: 'UTC', label: 'UTC' },
];

/**
 * System Settings Form Component
 */
export function SystemSettings({
  settings,
  onChange,
  onSave,
  disabled,
  paginationError,
}: SystemSettingsProps) {
  // Local state for pagination input to allow clearing
  const [paginationValue, setPaginationValue] = React.useState<string>(
    String(settings?.pagination_size || 20)
  );

  // Sync with external settings when they change
  React.useEffect(() => {
    setPaginationValue(String(settings?.pagination_size || 20));
  }, [settings?.pagination_size]);

  const handleLanguageChange = (value: string) => {
    onChange({ language: value as Language });
  };

  const handleTimezoneChange = (value: string) => {
    onChange({ timezone: value });
  };

  const handleDateFormatChange = (value: string) => {
    onChange({ date_format: value as DateFormat });
  };

  const handleCurrencyChange = (value: string) => {
    onChange({ currency: value as Currency });
  };

  const handlePaginationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    setPaginationValue(rawValue);

    // Only update parent state when we have a valid number
    const numValue = parseInt(rawValue, 10);
    if (!isNaN(numValue)) {
      onChange({ pagination_size: numValue });
    }
  };

  const handleNotificationsChange = (checked: boolean) => {
    onChange({ enable_notifications: checked });
  };

  const handleAuditLogChange = (checked: boolean) => {
    onChange({ enable_audit_log: checked });
  };

  return (
    <div data-testid="system-settings" className="space-y-6">
      {/* Localization */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Локализация</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Language */}
          <div className="space-y-2">
            <Label htmlFor="system-language">Язык</Label>
            <Select
              value={settings?.language || 'ru'}
              onValueChange={handleLanguageChange}
              disabled={disabled}
            >
              <SelectTrigger
                id="system-language"
                data-testid="system-language-select"
                className="w-full"
              >
                <SelectValue placeholder="Выберите язык" />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <Label htmlFor="system-timezone">Часовой пояс</Label>
            <Select
              value={settings?.timezone || 'Europe/Moscow'}
              onValueChange={handleTimezoneChange}
              disabled={disabled}
            >
              <SelectTrigger
                id="system-timezone"
                data-testid="system-timezone-select"
                className="w-full"
              >
                <SelectValue placeholder="Выберите часовой пояс" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Format */}
          <div className="space-y-2">
            <Label htmlFor="system-date-format">Формат даты</Label>
            <Select
              value={settings?.date_format || 'DD.MM.YYYY'}
              onValueChange={handleDateFormatChange}
              disabled={disabled}
            >
              <SelectTrigger
                id="system-date-format"
                data-testid="system-date-format-select"
                className="w-full"
              >
                <SelectValue placeholder="Выберите формат даты" />
              </SelectTrigger>
              <SelectContent>
                {DATE_FORMATS.map((format) => (
                  <SelectItem key={format.value} value={format.value}>
                    {format.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Currency */}
          <div className="space-y-2">
            <Label htmlFor="system-currency">Валюта</Label>
            <Select
              value={settings?.currency || 'RUB'}
              onValueChange={handleCurrencyChange}
              disabled={disabled}
            >
              <SelectTrigger
                id="system-currency"
                data-testid="system-currency-select"
                className="w-full"
              >
                <SelectValue placeholder="Выберите валюту" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((currency) => (
                  <SelectItem key={currency.value} value={currency.value}>
                    {currency.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Display Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Настройки отображения</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Pagination Size */}
          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="system-pagination">Размер страницы</Label>
              <InfoTooltip text="Количество элементов на странице (10-100)" />
            </div>
            <Input
              id="system-pagination"
              data-testid="system-pagination-input"
              type="number"
              value={paginationValue}
              onChange={handlePaginationChange}
              disabled={disabled}
              min={10}
              max={100}
            />
            {paginationError && (
              <p data-testid="pagination-error" className="text-sm text-red-600">
                {paginationError}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Feature Toggles — hidden, not removed */}
      {false && <Card>
        <CardHeader>
          <CardTitle className="text-lg">Функции</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Notifications */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="system-notifications"
              data-testid="system-notifications-toggle"
              checked={settings?.enable_notifications ?? true}
              onCheckedChange={handleNotificationsChange}
              disabled={disabled}
            />
            <Label htmlFor="system-notifications" className="cursor-pointer">
              Включить уведомления
            </Label>
          </div>
          <p className="text-sm text-gray-500 ml-6">
            Получать уведомления о важных событиях в приложении и по email
          </p>

          {/* Audit Log */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="system-audit"
              data-testid="system-audit-toggle"
              checked={settings?.enable_audit_log ?? true}
              onCheckedChange={handleAuditLogChange}
              disabled={disabled}
            />
            <Label htmlFor="system-audit" className="cursor-pointer">
              Включить журнал аудита
            </Label>
          </div>
          <p className="text-sm text-gray-500 ml-6">
            Отслеживать все изменения смет и настроек
          </p>
        </CardContent>
      </Card>}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          data-testid="save-system-button"
          onClick={onSave}
          disabled={disabled}
        >
          Сохранить системные настройки
        </Button>
      </div>
    </div>
  );
}
